import { type PropsWithChildren, useEffect, useMemo } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { TolgeeProvider } from '@simsm/ui-i18n';
import { ToastProvider, installAgePopoverPlacement } from '@simsm/ui-shared';
import { createQueryClient } from './query-client';
import { getFrontofficeTolgee } from './i18n';
import { followOtherTabs } from './active-registrar';
import { ConfirmProvider } from '../components/ConfirmDialog/ConfirmDialog';

// `@simsm/ui-shared` carries no translation runtime — every string reaching it is
// already resolved — so the toast host's close label is resolved on this side of
// the boundary, once, and follows the interface language like any other key.
function ToastHost({ children }: PropsWithChildren): JSX.Element {
  const { t } = useTranslate();
  return <ToastProvider closeLabel={t('common.close')}>{children}</ToastProvider>;
}

export function AppProviders({ children }: PropsWithChildren): JSX.Element {
  const queryClient = useMemo(() => createQueryClient(), []);
  const tolgee = getFrontofficeTolgee();

  // AGE-BUG(popovers-do-not-flip) — every date picker in the document gets a placement
  // that reads the viewport (`mud-select` flips itself since MUD 1.2). Installed once here
  // rather than at each call site: a fix applied per component is a fix the next screen
  // forgets.
  useEffect(() => installAgePopoverPlacement(), []);

  // CF-27: the tabs of one session work on one active Registrar, so a selection
  // made in another tab is applied here too (docs/shape-ops-tenant-selector.md §9.2).
  useEffect(() => followOtherTabs(queryClient), [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <TolgeeProvider tolgee={tolgee} fallback="Loading…">
        {/* Inside Tolgee: every toast carries copy the call site resolved, and
            the host's own close label is resolved once, here, because it is
            chrome rather than message content. */}
        <ToastHost>
          <ConfirmProvider>{children}</ConfirmProvider>
        </ToastHost>
      </TolgeeProvider>
    </QueryClientProvider>
  );
}
